import React, { useEffect, useState } from 'react';
import { DatePicker, Button, message, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import api from '../../../services/api';
import ReportLayout from '../ReportLayout';
import '../report-common.css'; // CSS chung cho tất cả báo cáo

const { RangePicker } = DatePicker;

interface StockOutItem {
  id: number;
  medicine_name: string;
  quantity: number;
  unit_price: number;
  lot_number?: string;
  unit?: string;
}
interface StockOutVoucher {
  id: number;
  voucher_number: string;
  created_at: string;
  items: StockOutItem[];
}
interface StockInItem {
  id: number;
  medicine_name: string;
  quantity: number;
  unit_price: number;
  lot_number?: string;
  unit?: string;
}
interface StockInVoucher {
  id: number;
  voucher_number: string;
  created_at: string;
  items: StockInItem[];
}

const RevenueReport: React.FC = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState<StockOutVoucher[]>([]);
  const [cogs, setCogs] = useState<StockInVoucher[]>([]);
  // Chi phí hoạt động từ bảng expenses
  const [operatingExpenses, setOperatingExpenses] = useState<any[]>([]);

  // Tổng hợp
  const totalRevenue = sales.reduce(
    (sum, v) => sum + v.items.reduce((s, i) => s + i.quantity * i.unit_price, 0),
    0
  );
  const totalCogs = cogs.reduce(
    (sum, v) => sum + v.items.reduce((s, i) => s + i.quantity * i.unit_price, 0),
    0
  );
  const grossProfit = totalRevenue - totalCogs;
  // Gom nhóm chi phí theo danh mục
  const expensesByCategory = operatingExpenses.reduce((acc: any, expense: any) => {
    const categoryName = expense.category_name || 'Không phân loại';
    if (!acc[categoryName]) {
      acc[categoryName] = {
        name: categoryName,
        total: 0
      };
    }
    acc[categoryName].total += expense.amount || 0;
    return acc;
  }, {});
  
  const totalOperatingExpenses = operatingExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const netProfit = grossProfit - totalOperatingExpenses;

  // Lấy danh sách phiếu xuất/nhập và chi tiết
  const fetchData = async () => {
    setLoading(true);
    try {
      const start_date = range[0].format('YYYY-MM-DD');
      const end_date = range[1].format('YYYY-MM-DD');
      
      // Sử dụng API mới để lấy tất cả dữ liệu trong 1 lần gọi
      const response = await api.get(`/api/reports/revenue-detail?start_date=${start_date}&end_date=${end_date}`);
      const data = response.data.data;
      
      setSales(data.sales || []);
      setCogs(data.cogs || []);
      
      // Lấy chi phí hoạt động từ bảng expenses
      try {
        const expensesResponse = await api.get(`/api/expenses?date_from=${start_date}&date_to=${end_date}&status=paid`);
        const expenses = expensesResponse.data || [];
        setOperatingExpenses(expenses);
      } catch (expenseErr) {
        console.error('Lỗi khi lấy chi phí:', expenseErr);
        setOperatingExpenses([]);
      }
    } catch (err) {
      message.error('Lỗi khi lấy dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  // Sidebar content
  const sidebar = (
    <>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ color: '#2c3e50', margin: 0, fontSize: 18 }}>Chọn khoảng thời gian</h3>
        <div style={{ margin: '16px 0' }}>
          <RangePicker
            value={range}
            onChange={val => val && setRange(val as [Dayjs, Dayjs])}
            allowClear={false}
            style={{ width: '100%' }}
          />
        </div>
        <Button type="primary" onClick={fetchData} loading={loading} block>Xem báo cáo</Button>
      </div>
    </>
  );

  // Nội dung báo cáo
  const reportContent = (
    <Spin spinning={loading} tip="Đang tải dữ liệu...">
      <div id="revenue-report-print" className="reportContainer">
        <div className="reportHeader">
          <h1 className="reportTitle">BÁO CÁO KẾT QUẢ KINH DOANH</h1>
          <div className="reportSubtitle">
            Kỳ báo cáo: {range[0].format('DD/MM/YYYY')} - {range[1].format('DD/MM/YYYY')}
          </div>
        </div>
      {/* I. Doanh thu bán hàng */}
      <div className="reportTable">
        <h2 style={{ fontSize: '1.2em', color: '#3498db', borderBottom: '2px solid #3498db', paddingBottom: 5, marginBottom: 15 }}>I. Doanh Thu Bán Hàng (từ Phiếu Xuất Kho)</h2>
        <div className="tableWrapper">
          <table className="table">
            <thead className="tableHeader">
              <tr>
                <th className="headerCell">Ngày</th>
                <th className="headerCell">Số Phiếu Xuất</th>
                <th className="headerCell">Tên Hàng Hóa</th>
                <th className="headerCell">Số Lượng</th>
                <th className="headerCell">Đơn Giá</th>
                <th className="headerCell">Thành Tiền</th>
              </tr>
            </thead>
            <tbody>
              {sales.flatMap(voucher =>
                voucher.items.map((item, idx) => (
                  <tr key={voucher.id + '-' + item.id + '-' + idx} className={idx % 2 === 0 ? 'evenRow' : 'oddRow'}>
                    <td className="detailCell">{dayjs(voucher.created_at).format('DD/MM/YYYY')}</td>
                    <td className="detailCell">{voucher.voucher_number}</td>
                    <td className="detailCell">{item.medicine_name}</td>
                    <td className="detailCell">{item.quantity}</td>
                    <td className="detailCell">{item.unit_price.toLocaleString('vi-VN')}đ</td>
                    <td className="detailCell">{(item.quantity * item.unit_price).toLocaleString('vi-VN')}đ</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <table className="summary-table" style={{ width: '100%', marginTop: 20 }}>
          <tbody>
            <tr>
              <td style={{ ...summaryLabelStyle }}><strong>Tổng Doanh Thu (A)</strong></td>
              <td style={{ ...summaryValueStyle, color: '#27ae60', fontWeight: 'bold' }}><strong>{totalRevenue.toLocaleString('vi-VN')}đ</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* II. Giá vốn hàng bán */}
      <div className="reportTable">
        <h2 style={{ fontSize: '1.2em', color: '#3498db', borderBottom: '2px solid #3498db', paddingBottom: 5, marginBottom: 15 }}>II. Giá Vốn Hàng Bán (từ Phiếu Nhập Kho)</h2>
        <div className="tableWrapper">
          <table className="table">
            <thead className="tableHeader">
              <tr>
                <th className="headerCell">Tên Hàng Hóa Bán</th>
                <th className="headerCell">Số Lượng</th>
                <th className="headerCell">Giá Vốn Đơn Vị</th>
                <th className="headerCell">Tổng Giá Vốn</th>
              </tr>
            </thead>
            <tbody>
              {cogs.flatMap(voucher =>
                voucher.items.map((item, idx) => (
                  <tr key={voucher.id + '-' + item.id + '-' + idx} className={idx % 2 === 0 ? 'evenRow' : 'oddRow'}>
                    <td className="detailCell">{item.medicine_name}</td>
                    <td className="detailCell">{item.quantity}</td>
                    <td className="detailCell">{item.unit_price.toLocaleString('vi-VN')}đ</td>
                    <td className="detailCell">{(item.quantity * item.unit_price).toLocaleString('vi-VN')}đ</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <table className="summary-table" style={{ width: '100%', marginTop: 20 }}>
          <tbody>
            <tr>
              <td style={{ ...summaryLabelStyle }}><strong>Tổng Giá Vốn Hàng Bán (B)</strong></td>
              <td style={{ ...summaryValueStyle, color: '#e74c3c', fontWeight: 'bold' }}><strong>{totalCogs.toLocaleString('vi-VN')}đ</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* III. Lợi nhuận gộp */}
      <div className="section" style={{ marginBottom: 30 }}>
        <table className="summary-table" style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td style={{ ...summaryLabelStyle, fontSize: '1.1em' }}><strong>Lợi Nhuận Gộp (C = A - B)</strong></td>
              <td style={{ ...summaryValueStyle, color: '#27ae60', fontWeight: 'bold', fontSize: '1.1em' }}><strong>{grossProfit.toLocaleString('vi-VN')}đ</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* IV. Chi phí hoạt động */}
      <div className="reportTable">
        <h2 style={{ fontSize: '1.2em', color: '#3498db', borderBottom: '2px solid #3498db', paddingBottom: 5, marginBottom: 15 }}>IV. Chi Phí Hoạt Động</h2>
        <div className="tableWrapper">
          <table className="table">
            <thead className="tableHeader">
              <tr>
                <th className="headerCell">Danh Mục Chi Phí</th>
                <th className="headerCell">Tổng Số Tiền</th>
              </tr>
            </thead>
            <tbody>
              {operatingExpenses.length === 0 ? (
                <tr>
                  <td colSpan={2} className="emptyCell">Không có dữ liệu chi phí hoạt động.</td>
                </tr>
              ) : (
                Object.values(expensesByCategory).map((category: any, idx) => (
                  <tr key={category.name + '-' + idx} className={idx % 2 === 0 ? 'evenRow' : 'oddRow'}>
                    <td className="detailCell">{category.name}</td>
                    <td className="detailCell">{category.total.toLocaleString('vi-VN')}đ</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <table className="summary-table" style={{ width: '100%', marginTop: 20 }}>
          <tbody>
            <tr>
              <td style={{ ...summaryLabelStyle }}><strong>Tổng Chi Phí Hoạt Động (D)</strong></td>
              <td style={{ ...summaryValueStyle, color: '#e74c3c', fontWeight: 'bold' }}><strong>{totalOperatingExpenses.toLocaleString('vi-VN')}đ</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* V. Lợi nhuận ròng */}
      <div className="section" style={{ marginBottom: 30 }}>
        <table className="summary-table" style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td className="net-profit" style={{ ...summaryLabelStyle, fontSize: '1.2em', color: '#2980b9', background: '#f0f8ff', padding: 10, borderRadius: 5 }}><strong>Lợi Nhuận Ròng (C - D)</strong></td>
              <td className="net-profit" style={{ ...summaryValueStyle, fontSize: '1.2em', color: '#2980b9', background: '#f0f8ff', padding: 10, borderRadius: 5 }}><strong>{netProfit.toLocaleString('vi-VN')}đ</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Footer */}
      <div className="footer" style={{ textAlign: 'center', marginTop: 40, fontSize: '0.9em', color: '#95a5a6' }}>
        <p>Ngày lập báo cáo: {dayjs().format('DD/MM/YYYY')}</p>
        <p><strong>Người Lập Báo Cáo</strong></p>
        <br /><br />
        <p>[Ký và ghi rõ họ tên]</p>
      </div>
      </div>
    </Spin>
  );

  // In báo cáo: chỉ in phần nội dung báo cáo
  const handlePrint = () => {
    window.print();
  };

  return (
    <ReportLayout sidebar={sidebar} onPrint={handlePrint} printId="revenue-report-print">
      {reportContent}
    </ReportLayout>
  );
};

// Removed old styles - now using CSS classes
const summaryLabelStyle: React.CSSProperties = {
  fontWeight: 'bold',
  textAlign: 'right',
  width: '70%',
  border: 'none',
  padding: '8px 12px',
};
const summaryValueStyle: React.CSSProperties = {
  textAlign: 'right',
  border: 'none',
  padding: '8px 12px',
};

export default RevenueReport;
